import React from 'react';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const PrivacyPolicy = () => {
  useDocumentMetadata({
    title: 'Privatlivspolitik | MedicCare',
    description: 'Læs om, hvordan MedicCare indsamler, beskytter og behandler personoplysninger i overensstemmelse med gældende GDPR-lovgivning.'
  });

  return (
    <div className="min-h-screen bg-slate-50 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/" 
          className="inline-flex items-center text-[#2c3223] hover:text-brand-dark font-bold text-xs uppercase tracking-widest mb-12 transition-colors bg-white px-6 py-3 rounded-full shadow-sm border border-slate-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Tilbage til forsiden
        </Link>

        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 overflow-hidden border border-slate-100">
          <div className="bg-[#2c3223] p-8 md:p-12 text-white">
            <div className="flex items-center gap-6 mb-6">
              <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-md">
                <ShieldCheck className="w-10 h-10 text-brand-light" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Privatlivspolitik</h1>
                <p className="text-brand-soft/80 mt-2 font-medium">Hvordan vi beskytter og behandler dine data hos MedicCare</p>
              </div>
            </div>
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-brand-light/60">
              Sidst opdateret: 20. Maj 2026
            </div>
          </div>

          <div className="p-8 md:p-12 prose prose-slate max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-soft text-[#2c3223] text-sm">1</span>
                Generelt
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Denne privatlivspolitik beskriver, hvordan MedicCare indsamler og behandler personoplysninger i forbindelse med dit besøg på vores hjemmeside og brug af vores ydelser. Vi er forpligtet til at beskytte dit privatliv og sikre, at dine personlige data behandles sikkert og i overensstemmelse med gældende lovgivning, herunder EU's databeskyttelsesforordning (GDPR).
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-soft text-[#2c3223] text-sm">2</span>
                Dataansvarlig
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                MedicCare er dataansvarlig for behandlingen af de personoplysninger, vi har modtaget om dig. Du finder vores kontaktoplysninger nedenfor:
              </p>
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Virksomhed</h4>
                  <p className="font-bold text-[#2c3223] text-lg">MedicCare A/S</p>
                  <p className="text-slate-600">Jyllandsgade 30 1</p>
                  <p className="text-slate-600">6400 Sønderborg, Danmark</p>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Kontakt</h4>
                  <p className="text-slate-600"><span className="font-semibold">Telefon:</span> <a href="tel:+4540830830" className="hover:text-brand-soft transition-colors">+45 40 83 08 30</a></p>
                  <p className="text-slate-600"><span className="font-semibold">Email:</span> <a href="mailto:info@mediccare.dk" className="hover:text-brand-soft transition-colors underline decoration-brand-soft/30">info@mediccare.dk</a></p>
                  <p className="text-slate-600"><span className="font-semibold">Kontaktperson:</span> Johnny Andersen</p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-soft text-[#2c3223] text-sm">3</span>
                Dataindsamling
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Vi indsamler oplysninger, som du selv giver os, når du interagerer med vores platform:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 list-none p-0 m-0">
                {[
                  'Udfyldelse af kontaktformularer',
                  'Bestilling af produktdemonstrationer',
                  'Kommunikation via e-mail eller telefon',
                  'Tilmeldelse til nyhedsbreve'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-xl text-slate-600 text-sm shadow-sm m-0">
                    <div className="w-2 h-2 rounded-full bg-brand-light shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-soft text-[#2c3223] text-sm">4</span>
                Formål og Retsgrundlag
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Behandlingen af dine oplysninger sker for at kunne:
              </p>
              <div className="space-y-4">
                <div className="p-4 border-l-4 border-brand-soft bg-brand-soft/30">
                  <p className="text-slate-700 text-sm leading-relaxed m-0">
                    Levere og optimere vores ydelser, herunder Medimi®Smart løsningen, besvare henvendelser og sikre overholdelse af gældende lovgivning inden for sundhedsområdet.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-soft text-[#2c3223] text-sm">5</span>
                Sikkerhed
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Vi har implementeret omfattende tekniske og organisatoriske sikkerhedsforanstaltninger for at beskytte dine data mod misbrug, tab eller uautoriseret adgang. Vi benytter kryptering og adgangskontrol som en fast del af vores databehandling.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-soft text-[#2c3223] text-sm">6</span>
                Dine Rettigheder
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Du har ret til at få indsigt i de oplysninger, vi behandler om dig, samt ret til at få dem rettet eller slettet i visse tilfælde. Du kan altid trække dit samtykke tilbage ved at kontakte os.
              </p>
              <div className="p-6 bg-[#2c3223] rounded-2xl text-white">
                <p className="text-sm font-medium mb-2">Har du spørgsmål til dine data?</p>
                <p className="text-brand-soft text-xs">Kontakt os på <a href="mailto:info@mediccare.dk" className="underline hover:text-white transition-colors">info@mediccare.dk</a> eller ring på <a href="tel:+4540830830" className="underline hover:text-white transition-colors">+45 40 83 08 30</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
